import {
  createAssemblyJob,
  pollAssemblyJob,
  MAX_ASSEMBLY_ITEMS,
} from '../../service/assemblyService';
import { getDocumentDetail } from '../../service/matter';
import {
  fetchMatterDetail,
  fetchMatterDetailList,
} from '../../slice/MatterDetailsSlice';

// Default output name: readable and unique enough ("Merged 2026-07-14 1712.pdf").
export const defaultOutputName = (now = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `Merged ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate()
  )} ${pad(now.getHours())}${pad(now.getMinutes())}.pdf`;
};

// The assembly API resolves documents by DMS unique_id ("D<uuid>") or raw
// pk. Legacy DocRio-catalog rows can have an empty unique_id (no sidecar),
// so fall back to the raw id.
export const assemblyDocumentId = (file) => {
  const uniqueId = typeof file?.unique_id === 'string' ? file.unique_id.trim() : '';
  if (uniqueId) return uniqueId;
  const rawId = file?.document_id ?? file?.id;
  return rawId != null && String(rawId).trim() !== '' ? String(rawId) : null;
};
/**
 * Handle when FileMergeModal proceed is clicked - add file to selected files list
 */
export const handleFileSelected = (
  file,
  setSelectedFiles,
  setShowFileMergeModal,
  setShowSelectedFilesModal,
  setSelectedFile
) => {
  // Add file to selected files list
  setSelectedFiles((prev) => {
    // Check if file already exists. Compare unique_id falling back to id —
    // records seeded from the table (or legacy docs without a sidecar
    // unique_id) would otherwise slip past a unique_id-only comparison and
    // end up in the packet twice.
    const identityOf = (record) => record?.unique_id || record?.id || null;
    const incomingIdentity = identityOf(file.record);
    const exists =
      incomingIdentity != null &&
      prev.some((f) => identityOf(f) === incomingIdentity);

    if (exists) {
      return prev;
    }
    // Ensure a stable id for drag-and-drop keys. Prefer unique_id, then
    // the record's OWN id — overwriting a legacy (sidecar-less) record's
    // real pk with a random UUID would send garbage to the assembly API
    // (Bugbot #236). Random fallback only for records with no identity at
    // all, which can't be merged anyway.
    const fileWithId = {
      ...file.record,
      id: file.record.unique_id || file.record.id || crypto.randomUUID(),
    };
    return [...prev, fileWithId];
  });

  // Close file merge modal and show selected files modal
  setShowFileMergeModal(false);
  setShowSelectedFilesModal(true);
  setSelectedFile('');
};

/**
 * Handle when user clicks "Add Files" in SelectedFilesModal
 */
export const handleAddFiles = (
  setShowSelectedFilesModal,
  setShowFileMergeModal
) => {
  setShowSelectedFilesModal(false);
  setShowFileMergeModal(true);
};

/**
 * Handle removing a file from selected files
 */
export const handleRemoveFile = (index, setSelectedFiles) => {
  setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
};

/**
 * Handle reordering files
 */
export const handleReorder = (newOrder, setSelectedFiles) => {
  setSelectedFiles(newOrder);
};

/**
 * Handle combine files — server-side document assembly (async).
 *
 * Flow: POST /documents/assemble/ → poll the job → on completed, load the
 * new document's detail (unique_id + relative_path for the "Open file"
 * button) and refresh the sidebar tree + the document list the user has
 * open. Progress is advanced on poll ticks (queued ≈ 15%, running climbs
 * toward 90%, terminal = 100%) since the job reports states, not percents.
 */
export const handleCombineFiles = async ({
  selectedFiles,
  currentMatterId,
  outputName,
  destinationFolder,
  listRefreshArgs,
  setMergeProgress,
  setIsMerging,
  setIsMergeComplete,
  setMergedFileUrl,
  setMergedFile,
  setMergeError,
  setShowFileMergeModal,
  setShowSelectedFilesModal,
  setSelectedFile,
  setSelectedFiles,
  onClose,
  dispatch
}) => {
  if (selectedFiles.length === 0) {
    return;
  }

  const items = selectedFiles
    .map((file) => assemblyDocumentId(file))
    .filter(Boolean)
    .map((documentId) => ({ document_id: documentId }));

  if (items.length === 0) {
    setMergeError('No valid document ids found in selected files');
    return;
  }

  // The reorder modal already requires two rows, but a selected file whose
  // id couldn't be resolved would silently shrink the packet — a one-item
  // "merge" the user never asked for (Bugbot #236). Fail loudly instead.
  if (items.length < 2) {
    setMergeError(
      `Only ${items.length} of the ${selectedFiles.length} selected files could ` +
        'be identified — at least two are required to merge. Remove the ' +
        'unrecognized file and try again.'
    );
    return;
  }

  if (items.length > MAX_ASSEMBLY_ITEMS) {
    setMergeError(
      `You can merge up to ${MAX_ASSEMBLY_ITEMS} files at a time — remove ${
        items.length - MAX_ASSEMBLY_ITEMS
      } and try again.`
    );
    return;
  }

  if (!currentMatterId) {
    setMergeError('Matter ID is required');
    return;
  }

  const trimmedName = (outputName || '').trim();
  const finalOutputName = trimmedName
    ? trimmedName.toLowerCase().endsWith('.pdf')
      ? trimmedName
      : `${trimmedName}.pdf`
    : defaultOutputName();

  // Reset states
  setMergeProgress(0);
  setIsMerging(true);
  setIsMergeComplete(false);
  setMergedFileUrl(null);
  setMergeError(null);

  try {
    // Merges started while browsing a folder land IN that folder (matching
    // where the refreshed table will look); at the matter root the folder
    // is omitted and the output lands at the root.
    const { job_id: jobId } = await createAssemblyJob({
      matter: currentMatterId,
      outputName: finalOutputName,
      folder: destinationFolder || undefined,
      items,
    });

    // Close the pickers right away — the floating progress notification
    // takes over (the job runs server-side; nothing left to configure).
    setShowFileMergeModal(false);
    setShowSelectedFilesModal(false);
    setSelectedFile('');
    setSelectedFiles([]);
    if (onClose) {
      onClose();
    }

    let simulated = 15;
    const job = await pollAssemblyJob(jobId, {
      onTick: (tick) => {
        if (tick.status === 'queued') {
          setMergeProgress(15);
        } else if (tick.status === 'running') {
          simulated = Math.min(simulated + 7, 90);
          setMergeProgress(simulated);
        }
      },
    });

    if (job.status === 'failed') {
      throw new Error(job.error_detail || 'Failed to merge files');
    }

    // Fetch the created document so "Open file" can navigate to it.
    const detailResponse = await getDocumentDetail(job.output_document_id);
    const mergedDocument = detailResponse?.data;
    if (!mergedDocument?.relative_path) {
      throw new Error(
        'Merge finished but the new file could not be loaded — refresh the matter to find it.'
      );
    }

    // Refresh both the sidebar tree and the open document list (same dual
    // pattern as delete/rename), keeping the list's page/sort/folder scope.
    dispatch(fetchMatterDetail(currentMatterId));
    dispatch(
      fetchMatterDetailList({
        id: currentMatterId,
        page: listRefreshArgs?.page || 1,
        sort_direction: listRefreshArgs?.sort_direction,
        sort_by: listRefreshArgs?.sort_by,
        ...(listRefreshArgs?.folder ? { folder: listRefreshArgs.folder } : {}),
      })
    );

    setMergedFileUrl(mergedDocument.relative_path);
    setMergedFile(mergedDocument);
    setIsMergeComplete(true);
    setIsMerging(false);
    setMergeProgress(100);
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      firstSerializerError(error?.response?.data) ||
      error?.message ||
      'Failed to merge files';
    setMergeError(errorMessage);
    setIsMerging(false);
    setIsMergeComplete(false);
    setMergeProgress(0);
  }
};

// DRF serializer errors come back as {"field": ["msg"]} or nested per-item
// shapes; surface the first human-readable string instead of "[object
// Object]".
export const firstSerializerError = (data) => {
  if (!data || typeof data !== 'object') return null;
  for (const value of Object.values(data)) {
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      for (const entry of value) {
        if (typeof entry === 'string') return entry;
        const nested = firstSerializerError(entry);
        if (nested) return nested;
      }
    } else {
      const nested = firstSerializerError(value);
      if (nested) return nested;
    }
  }
  return null;
};


/**
 * Handle closing merge notification
 */
export const handleCloseMergeNotification = (
  setIsMerging,
  setIsMergeComplete,
  setMergedFileUrl,
  setMergeProgress,
  setMergeError
) => {
  setIsMerging(false);
  setIsMergeComplete(false);
  setMergedFileUrl(null);
  setMergeProgress(0);
  setMergeError(null);
};

/**
 * Handle cancel
 */
export const handleCancel = (handleClose) => {
  handleClose();
};

/**
 * Close all modals and reset state
 */
export const handleClose = (
  setShowFileMergeModal,
  setShowSelectedFilesModal,
  setSelectedFile,
  setSelectedFiles,
  onClose
) => {
  setShowFileMergeModal(false);
  setShowSelectedFilesModal(false);
  setSelectedFile('');
  setSelectedFiles([]);
  if (onClose) {
    onClose();
  }
};

